import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import type { Database } from "@/integrations/supabase/types";

type RootCause = Database["public"]["Tables"]["sentinel_root_causes"]["Row"];

interface RootCauseTreeProps {
  rootCauses: RootCause[];
}

export function RootCauseTree({ rootCauses }: RootCauseTreeProps) {
  if (rootCauses.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center">
        <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Nenhuma análise de causa raiz disponível.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      {rootCauses.map(rc => {
        const candidates = (rc.candidates as Array<{ id: string; type: string; description: string; confidence: number }>) || [];
        const confidencePct = (rc.confidence ?? 0) * 100;

        return (
          <Card key={rc.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-500" />
                  Causa Raiz
                </CardTitle>
                <Badge variant={confidencePct > 70 ? "default" : confidencePct > 40 ? "secondary" : "outline"} className="text-xs">
                  Confiança: {confidencePct.toFixed(0)}%
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-foreground">{rc.suspected_cause}</p>
              <Progress value={confidencePct} className="h-1.5" />
              <p className="text-xs text-muted-foreground">{new Date(rc.created_at).toLocaleString("pt-BR")}</p>

              {candidates.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-xs font-medium text-muted-foreground">Candidatos ({candidates.length}):</p>
                  {candidates.slice(0, 5).map((c, i) => (
                    <div key={c.id || i} className="flex items-center gap-2 text-xs p-2 bg-muted rounded">
                      <Badge variant="outline" className="text-[10px] shrink-0">{c.type}</Badge>
                      <span className="flex-1 text-muted-foreground truncate">{c.description}</span>
                      <span className="text-muted-foreground shrink-0">{(c.confidence * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
